import { useEffect, useState } from "react";
import { useData } from "../context/DataContext";
import api from "../lib/api";
import { toast } from "sonner";
import { X, Download, RotateCcw, HardDrive, AlertTriangle, Save, Search } from "lucide-react";
import { fmtMoney } from "../lib/format";

// Backups list — merges Mongo snapshots (auto-taken on every re-feed) with
// file-based on-disk backups (like the pre-incident one). Each row can be
// downloaded (files) or restored (both). Includes a "Snapshot now" button
// for on-demand manual backups.
export default function BackupsDialog({ open, onClose }) {
  const { refresh } = useData();
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmRow, setConfirmRow] = useState(null);
  const [audit, setAudit] = useState(null);
  const [auditing, setAuditing] = useState(false);

  // Read-only integrity check for the 2026-06 sub-consultant loss bug.
  const runAudit = async () => {
    setAuditing(true);
    try {
      const res = await api.get("/data/audit/sub-consultants");
      setAudit(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Audit failed");
    } finally { setAuditing(false); }
  };

  const load = async () => {
    try {
      const res = await api.get("/data/backups");
      const merged = [
        ...(res.data.fileBackups || []).map((b) => ({ ...b, kind: "file" })),
        ...(res.data.mongoBackups || []).map((b) => ({ ...b, kind: "mongo" })),
      ].sort((a, b) => (b.createdAt || "").localeCompare(a.createdAt || ""));
      setRows(merged);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Could not load backups");
    }
  };

  useEffect(() => { if (open) load(); }, [open]);
  if (!open) return null;

  const takeManual = async () => {
    setBusy(true);
    try {
      const res = await api.post("/data/snapshot");
      toast.success(`Snapshot saved · ${res.data.project_count} projects · ${fmtMoney(res.data.raw_total)}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Snapshot failed");
    } finally { setBusy(false); }
  };

  const doRestore = async (row) => {
    setBusy(true);
    try {
      const res = await api.post(`/data/restore/${encodeURIComponent(row.id)}`);
      toast.success(`Restored · ${res.data.projectCount} projects`, {
        description: "Previous state was snapshotted first — you can undo from here.",
      });
      setConfirmRow(null);
      await refresh?.();
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Restore failed");
    } finally { setBusy(false); }
  };

  const downloadFile = (row) => {
    const token = localStorage.getItem("mm_token");
    if (!token) { toast.error("Not signed in"); return; }
    const link = document.createElement("a");
    link.href = `${process.env.REACT_APP_BACKEND_URL}${row.downloadUrl}`;
    // Bearer needs to be inline via fetch → object URL for streams.
    fetch(link.href, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = row.id;
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
        URL.revokeObjectURL(url);
      })
      .catch(() => toast.error("Download failed"));
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4 animate-fade"
      onClick={onClose} data-testid="backups-dialog-backdrop">
      <div className="bg-white w-full max-w-4xl max-h-[92vh] flex flex-col border border-[var(--rule-strong)] animate-fade-up"
        onClick={(e) => e.stopPropagation()} data-testid="backups-dialog">
        <div className="flex items-center justify-between p-6 border-b border-[var(--rule)]">
          <div>
            <div className="eyebrow">Data · backups</div>
            <h3 className="font-display text-lg mt-1">Dataset backups</h3>
            <div className="t-caption mt-1 max-w-lg">
              Every re-feed auto-snapshots the previous state before writing.
              Last 20 kept. Snapshots belong to this environment — restoring
              from a backup file is disabled.
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-[var(--paper-soft)]" data-testid="backups-close">
            <X size={16} />
          </button>
        </div>

        <div className="flex items-center gap-2 p-4 border-b border-[var(--rule)] flex-wrap">
          <button onClick={takeManual} disabled={busy}
            className="inline-flex items-center gap-2 px-3 py-2 border border-[var(--rule-strong)] text-xs font-display tracked-tight uppercase hover:bg-[var(--paper-soft)] disabled:opacity-40"
            data-testid="backups-snapshot-now">
            <Save size={12} /> Snapshot now
          </button>
          <span className="text-[11px] font-mono-num text-[var(--soft-fg)]">
            Grab a manual backup of the current dataset. Useful right after a good save.
          </span>
        </div>

        <div className="p-4 border-b border-[var(--rule)]">
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={runAudit} disabled={auditing}
              className="inline-flex items-center gap-2 px-3 py-2 border border-[var(--rule-strong)] text-xs font-display tracked-tight uppercase hover:bg-[var(--paper-soft)] disabled:opacity-40"
              data-testid="audit-sub-consultants">
              <Search size={12} /> {auditing ? "Checking…" : "Check sub-consultant rows"}
            </button>
            <span className="text-[11px] font-mono-num text-[var(--soft-fg)]">
              Read-only. Compares every snapshot against the live data and reports sub-consultant rows that went missing. Restores nothing.
            </span>
          </div>
          {audit && (
            <div className="mt-3 border border-[var(--rule)] p-3" data-testid="audit-result">
              <div className="text-[11px] font-mono-num text-[var(--muted-fg)]">
                {audit.snapshotsChecked} snapshots · {audit.projectsChecked} projects · {audit.subRowsLive} sub-consultant rows live now
              </div>
              {audit.clean ? (
                <div className="mt-2 text-xs text-[#2C4A38] font-mono-num" data-testid="audit-clean">
                  No missing sub-consultant rows found — nothing to restore.
                </div>
              ) : (
                <div className="mt-2 space-y-2" data-testid="audit-affected">
                  <div className="text-xs text-[#6B2F2A] font-mono-num inline-flex items-center gap-2">
                    <AlertTriangle size={12} /> {audit.affectedProjects.length} project(s) lost sub-consultant rows. Nothing restored — report this before acting.
                  </div>
                  {audit.affectedProjects.map((p) => (
                    <div key={p.projectId} className="text-[11px] font-mono-num border-t border-[var(--rule)] pt-2"
                      data-testid={`audit-project-${p.projectId}`}>
                      <div className="text-[var(--ink)]">
                        {p.projectNumber} · {p.projectName} — {p.subRowsInSnapshot} in snapshot vs {p.subRowsNow} now
                      </div>
                      {p.rows.map((r, i) => (
                        <div key={`${p.projectId}-${i}`} className="text-[var(--soft-fg)] pl-3">
                          {r.stage || "Sub consultants"} · CV {fmtMoney(r.contractValue || 0)} ·
                          fees {Object.entries(r.revenue).map(([k, v]) => `${k} ${fmtMoney(v)}`).join(", ") || "—"} ·
                          invoiced {Object.entries(r.invoiced).map(([k, v]) => `${k} ${fmtMoney(v)}`).join(", ") || "—"}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex-1 overflow-auto thin-scroll">
          {rows === null ? (
            <div className="p-8 text-center eyebrow text-[var(--soft-fg)]">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="p-8 text-center eyebrow text-[var(--soft-fg)]" data-testid="backups-empty">No backups yet</div>
          ) : (
            <table className="w-full text-xs border-collapse" data-testid="backups-table">
              <thead>
                <tr className="border-b border-[var(--rule)] bg-[var(--paper-soft)]">
                  <th className="eyebrow text-left px-4 py-2">When</th>
                  <th className="eyebrow text-left px-4 py-2">Source</th>
                  <th className="eyebrow text-left px-4 py-2">Reason</th>
                  <th className="eyebrow text-left px-4 py-2">By</th>
                  <th className="eyebrow text-right px-4 py-2">Projects</th>
                  <th className="eyebrow text-right px-4 py-2">Firm raw</th>
                  <th className="eyebrow text-right px-4 py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={`${r.kind}-${r.id}`} className="border-b border-[var(--rule)]" data-testid={`backup-row-${r.id}`}>
                    <td className="px-4 py-2 font-mono-num">{new Date(r.createdAt).toLocaleString("en-NZ")}</td>
                    <td className="px-4 py-2">
                      <span className={`chip ${r.kind === "file" ? "chip-tentative" : ""}`}>
                        <HardDrive size={9} /> {r.kind}
                      </span>
                    </td>
                    <td className="px-4 py-2 font-mono-num text-[var(--soft-fg)]">{r.reason || "—"}</td>
                    <td className="px-4 py-2 font-mono-num text-[var(--soft-fg)]">{r.createdBy || "—"}</td>
                    <td className="px-4 py-2 font-mono-num text-right">{r.projectCount}</td>
                    <td className="px-4 py-2 font-mono-num text-right">{fmtMoney(r.rawTotal, { compact: true })}</td>
                    <td className="px-4 py-2 text-right">
                      <div className="inline-flex items-center gap-1">
                        {r.kind === "file" && (
                          <button onClick={() => downloadFile(r)}
                            className="inline-flex items-center gap-1 px-2 py-1 border border-[var(--rule)] text-[10px] font-display tracked-tight uppercase hover:bg-[var(--paper-soft)]"
                            data-testid={`backup-download-${r.id}`}>
                            <Download size={10} /> download
                          </button>
                        )}
                        <button onClick={() => setConfirmRow(r)}
                          className="inline-flex items-center gap-1 px-2 py-1 border border-[var(--rule-strong)] text-[10px] font-display tracked-tight uppercase hover:bg-[var(--paper-soft)]"
                          data-testid={`backup-restore-${r.id}`}>
                          <RotateCcw size={10} /> restore
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {confirmRow && (
          <div className="p-5 border-t border-[#6B2F2A]/30 bg-[#FBF3F2] flex items-center justify-between gap-4 flex-wrap" data-testid="restore-confirm">
            <div className="text-xs font-mono-num text-[#6B2F2A] inline-flex items-start gap-2">
              <AlertTriangle size={13} className="shrink-0 mt-0.5" />
              <div>
                <strong>Restore from {confirmRow.id}?</strong> The current dataset will be replaced.
                A snapshot of what's live now will be saved first (reason: pre_restore).
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => setConfirmRow(null)}
                className="px-3 py-2 border border-[var(--rule)] text-xs font-display tracked-tight uppercase hover:bg-white"
                data-testid="restore-cancel">Cancel</button>
              <button onClick={() => doRestore(confirmRow)} disabled={busy}
                className="px-3 py-2 bg-[#6B2F2A] text-white text-xs font-display tracked-tight uppercase disabled:opacity-40"
                data-testid="restore-confirm-yes">
                {busy ? "Restoring…" : "Yes, restore"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
